import React, { useCallback, useEffect, useRef, useState } from "react";

import { fetchAdminTenancyRevenue } from "../../../api/adminData/tenancies";
import { fetchBexioTenancyInvoices } from "../../../api/adminData/bexio";
import { formatDateOnly } from "../../../utils/datetime";
import { formatChf } from "../tenants/tenantOverviewData";
import BexioTablePagination from "../../../components/admin/bexio/workspace/BexioTablePagination";
import StatusPill from "../../../components/admin/bexio/BexioInvoiceStatusPill";
import { Card, EmptyState, Field, show } from "./workspaceUi";
import { depositSecurityState } from "../deposits/depositsLabels";
import DepositBadge from "../deposits/DepositBadge";
import {
  PROVIDER_MISSING_LABEL,
  PROVIDER_MISSING_LABEL_SHORT,
  fmtInvoiceMoney,
} from "../../../components/admin/bexio/bexioFormat";
import {
  oneTimeBreakdownEntries,
  recurringMonthlyBreakdownEntries,
} from "../../../utils/tenancyRevenueBreakdown";
import {
  depositState,
  depositStateDate,
  linkSourceLabel,
  rentPeriodLabel,
} from "./tenancyFinance";

/**
 * The tenancy's Finanzen tab: what was AGREED, beside what Bexio HOLDS.
 *
 * TWO TRUTHS, SIDE BY SIDE, NEVER SUBTRACTED. Vantio owns the Soll — the
 * contract's revenue rows and its deposit. Bexio owns the Ist — the invoices
 * and their payment state. This renders both and computes NO delta between
 * them, because none can honestly be computed: an invoice carries a rent month
 * only when a draft-ledger row recorded one, so a "Soll minus Ist für Oktober"
 * would silently exclude every hand-mapped historical invoice and report the
 * gap as unpaid rent. The backend says the same thing in its own words —
 * `direct_delta_available` is `False` throughout, and the comparison mode is
 * `side_by_side_no_month_attribution`.
 *
 * NO PAGE-LOCAL TOTALS. "Monatsmiete" is the server's
 * `monthly_revenue_equivalent`; the revenue rows below it are a per-type
 * BREAKDOWN and carry no sum line, so nothing here can disagree with the
 * figure the list, the KPI cards and the unit surfaces already show. The
 * invoice table likewise prints each amount and adds none of them up.
 *
 * TWO REQUESTS, ONLY WHEN OPENED. The revenue rows and the first page of
 * invoices, for ONE selected tenancy, gated on `active` — a row the user only
 * glanced at costs nothing. Paging asks the server again rather than slicing a
 * list in the browser, so `total` and the page always describe the same set.
 *
 * PARTIAL FAILURE IS NOT TOTAL FAILURE. The two halves load and fail
 * independently: Bexio being unreachable must not hide the contract values,
 * which are Vantio's own data and still correct.
 */

const PAGE_SIZE = 10;

/**
 * The deposit's state as a word.
 *
 * Exported so the Übersicht card can show the same pill from the same rule —
 * a second derivation would be a second answer.
 */
export function DepositStatePill({ tenancy }) {
  const state = depositState(tenancy);
  if (!state) return null;
  // Words and tone from the shared deposits source; the type decides whether
  // "held" reads as received cash or a lodged bank deposit.
  const { label, tone } = depositSecurityState(
    state,
    tenancy?.tenant_deposit_type
  );
  const at = depositStateDate(tenancy);
  // `DepositBadge` already owns these tones; a second class map here is the
  // thing G10 exists to prevent.
  return (
    <DepositBadge tone={tone} testId="tenancy-deposit-state">
      {label}
      {at ? ` · ${formatDateOnly(at)}` : ""}
    </DepositBadge>
  );
}

/** One per-type line of the revenue breakdown. Never a total. */
function BreakdownRows({ entries, suffix, testid }) {
  return (
    <ul className="m-0 flex list-none flex-col gap-[6px] p-0" data-testid={testid}>
      {entries.map((e) => (
        <li
          key={e.typeKey}
          className="flex items-center justify-between gap-3 text-[13px]"
        >
          <span className="min-w-0 truncate text-foreground">{e.label}</span>
          <span className="shrink-0 tabular-nums text-foreground">
            {formatChf(e.total)}
            {suffix ? (
              <span className="text-muted-foreground">{suffix}</span>
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ── Karte A — Vertragswerte ─────────────────────────────────────────── */

function ContractValuesCard({ tenancy, revenue }) {
  // Both are BREAKDOWNS by stored type, from the shared library the tenant and
  // unit surfaces read. `one_time` is excluded from the recurring one
  // structurally, inside that library — never by a threshold or an amount
  // check here.
  const recurring = recurringMonthlyBreakdownEntries(revenue.rows);
  const oneTime = oneTimeBreakdownEntries(revenue.rows);

  return (
    <Card
      title="Vertragswerte"
      subtitle="Was vereinbart ist (Vantio). Keine Zahlungen — die stehen in Bexio."
    >
      <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-3">
        <Field
          label="Monatsmiete"
          value={
            tenancy?.monthly_revenue_equivalent == null
              ? null
              : formatChf(tenancy.monthly_revenue_equivalent)
          }
        />
        <div className="min-w-0">
          <dt className="text-[11.5px] leading-tight text-muted-foreground">
            Kaution
          </dt>
          <dd className="mt-[2px] flex flex-wrap items-center gap-2 break-words text-[13px] text-foreground">
            <span>
              {tenancy?.deposit_amount == null
                ? "—"
                : formatChf(tenancy.deposit_amount)}
            </span>
            <DepositStatePill tenancy={tenancy} />
          </dd>
        </div>
      </dl>

      <div className="mt-4 border-t border-border pt-3">
        {revenue.loading ? (
          <div
            aria-busy="true"
            data-testid="tenancy-revenue-loading"
            className="flex flex-col gap-2"
          >
            <span className="sr-only">Mietzins-Positionen werden geladen …</span>
            {[0, 1].map((i) => (
              <div
                key={i}
                aria-hidden="true"
                className="h-[24px] animate-pulse rounded-[6px] border border-border bg-muted/40"
              />
            ))}
          </div>
        ) : revenue.error ? (
          // Local and quiet: the two contract values above are already loaded
          // and correct, and hiding them because a second request failed would
          // lose data the panel actually has.
          <p
            role="alert"
            data-testid="tenancy-revenue-error"
            className="m-0 text-[12.5px] text-rose-700 dark:text-rose-300"
          >
            {revenue.error}
          </p>
        ) : recurring.length === 0 && oneTime.length === 0 ? (
          <EmptyState
            testId="tenancy-revenue-empty"
            title="Keine Mietzins-Positionen erfasst."
          />
        ) : (
          <div className="flex flex-col gap-4">
            {recurring.length > 0 ? (
              <div>
                <p className="m-0 mb-[6px] text-[11.5px] text-muted-foreground">
                  Wiederkehrend (monatlich)
                </p>
                <BreakdownRows
                  entries={recurring}
                  suffix=" / Mt."
                  testid="tenancy-revenue-recurring"
                />
              </div>
            ) : null}
            {/* Its own block, never merged into the monthly one: a one-time
                position is not a twelfth of anything, and folding it in would
                inflate the monthly figure by an amount that is paid once. */}
            {oneTime.length > 0 ? (
              <div>
                <p className="m-0 mb-[6px] text-[11.5px] text-muted-foreground">
                  Einmalig
                </p>
                <BreakdownRows
                  entries={oneTime}
                  testid="tenancy-revenue-one-time"
                />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Card>
  );
}

/* ── Karte B — Zugeordnete Bexio-Rechnungen ──────────────────────────── */

/** The month, or nothing at all — never a date standing in for one. */
function RentPeriodCell({ invoice }) {
  if (invoice.rent_period_conflict) {
    return (
      <span
        data-testid="tenancy-invoice-period-conflict"
        title="Mehrere Rechnungsentwürfe dieses Mietverhältnisses nennen unterschiedliche Monate für diese Rechnung."
        className="text-[12px] text-amber-800 dark:text-amber-300"
      >
        Monat unklar
      </span>
    );
  }
  const label = rentPeriodLabel(invoice.rent_period);
  if (!label) {
    // NOT an error and NOT a gap to fill: nothing ever recorded this invoice's
    // month. `issue_date` and `due_date` are visible in their own columns and
    // are deliberately not reused as one.
    return <span className="text-[12px] text-muted-foreground">—</span>;
  }
  return <span className="text-[12px] text-foreground">{label}</span>;
}

/**
 * A SECOND, INDEPENDENT dimension — never derived from, and never merged with,
 * `provider_replaced`.
 *
 * `provider_missing_at` is written only by a COMPLETED full provider inventory
 * — an operator-triggered backend action: Bexio was asked for this document and
 * did not have it. `provider_replaced` comes from the draft ledger instead, and
 * says a replacement was raised for the same month. Neither implies the other:
 * an inventory can mark an invoice missing that no ledger row ever replaced,
 * and the reader flags a replacement long before any inventory runs. So both
 * may appear on one row, and an invoice carrying only the first must not read
 * as an ordinary live receivable.
 *
 * The WORDS are the repo's single definition (`bexioFormat`), so this reads
 * exactly as the Bexio workspace list does — short label in the pill, long form
 * in the title. The one-line null check is restated rather than imported: the
 * page's `isProviderMissing` is pinned INSIDE `BexioInvoicesPage.jsx` by
 * `bexioProviderMissingGuards` (G10–G13), which reads that file's source, so
 * exporting it would break a merged slice's contract.
 *
 * Amber, not red, and never "gelöscht": nothing here is broken. The row is
 * correct history, the financial readers have already stopped counting it, and
 * what we persist is "an inventory looked and did not find it" — deletion is
 * only the likeliest reason, never one this system observed.
 */
function ProviderMissingTag({ invoice }) {
  if (invoice?.provider_missing_at == null) return null;
  return (
    <span
      data-testid="tenancy-invoice-provider-missing"
      title={PROVIDER_MISSING_LABEL}
      className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-[1px] text-[10px] font-semibold text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
    >
      {PROVIDER_MISSING_LABEL_SHORT}
    </span>
  );
}

function InvoiceRow({ invoice }) {
  return (
    <li
      data-testid={`tenancy-invoice-${invoice.id}`}
      className="flex flex-col gap-1 border-b border-border px-4 py-[10px] last:border-b-0"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-foreground">
          {show(invoice.document_nr)}
          {invoice.title ? (
            <span className="font-normal text-muted-foreground">
              {" · "}
              {invoice.title}
            </span>
          ) : null}
        </span>
        <StatusPill status={invoice.bexio_status} />
        <span className="shrink-0 tabular-nums text-[13px] text-foreground">
          {fmtInvoiceMoney(invoice.total, invoice.currency)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
        <RentPeriodCell invoice={invoice} />
        <span>Rechnungsdatum: {formatDateOnly(invoice.issue_date)}</span>
        <span>Fällig: {formatDateOnly(invoice.due_date)}</span>
        <span className="tabular-nums">
          Offen: {fmtInvoiceMoney(invoice.total_remaining, invoice.currency, {
            marker: false,
          })}
        </span>
        {linkSourceLabel(invoice.tenancy_link_source) ? (
          <span data-testid="tenancy-invoice-link-source">
            {linkSourceLabel(invoice.tenancy_link_source)}
          </span>
        ) : null}
        {/* The ledger says Bexio deleted this document and raised a
            replacement for the same month. Both rows carry that month's money
            and only one of them is real — so it is marked, never silently
            dropped and never counted beside its replacement. */}
        {invoice.provider_replaced ? (
          <span
            data-testid="tenancy-invoice-replaced"
            className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-[1px] text-[10px] font-semibold text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
          >
            Ersetzt
          </span>
        ) : null}
        {/* Independent of the mark above: both may be true, either may be
            true alone, and neither is computed from the other. */}
        <ProviderMissingTag invoice={invoice} />
      </div>
    </li>
  );
}

function InvoicesCard({ invoices, onOffsetChange }) {
  return (
    <section className="rounded-[10px] border border-border bg-card">
      <div className="border-b border-border px-4 py-[10px]">
        <h3 className="m-0 text-[13px] font-semibold text-foreground">
          Zugeordnete Bexio-Rechnungen
        </h3>
        <p className="m-0 mt-[2px] text-[11.5px] leading-snug text-muted-foreground">
          Was Bexio zu diesem Mietverhältnis führt. Ein Mietmonat steht nur dort,
          wo ein Rechnungsentwurf ihn festgehalten hat.
        </p>
      </div>

      {invoices.loading ? (
        <div className="flex flex-col gap-2 p-4" aria-busy="true" data-testid="tenancy-invoices-loading">
          <span className="sr-only">Rechnungen werden geladen …</span>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              aria-hidden="true"
              className="h-[38px] animate-pulse rounded-[7px] border border-border bg-muted/40"
            />
          ))}
        </div>
      ) : invoices.error ? (
        <p
          role="alert"
          data-testid="tenancy-invoices-error"
          className="m-0 px-4 py-4 text-[12.5px] text-rose-700 dark:text-rose-300"
        >
          {invoices.error}
        </p>
      ) : invoices.items.length === 0 ? (
        <div className="p-4">
          <EmptyState
            testId="tenancy-invoices-empty"
            title="Keine Bexio-Rechnungen für dieses Mietverhältnis."
          />
        </div>
      ) : (
        <>
          <ul className="m-0 list-none p-0" data-testid="tenancy-invoices">
            {invoices.items.map((inv) => (
              <InvoiceRow key={inv.id} invoice={inv} />
            ))}
          </ul>
          {/* The same offset pagination the Bexio workspace uses, against the
              same `limit`/`offset`/`total` contract. No page-size control: a
              detail panel is not a table view, and one fixed page keeps the
              request count predictable. */}
          <BexioTablePagination
            total={invoices.total}
            offset={invoices.offset}
            limit={PAGE_SIZE}
            onOffsetChange={onOffsetChange}
            noun="Rechnungen"
            busy={invoices.loading}
          />
        </>
      )}
    </section>
  );
}

/* ── The tab ─────────────────────────────────────────────────────────── */

export default function TenancyFinanceTab({ tenancy, active }) {
  const tenancyId = tenancy?.id;
  const [revenue, setRevenue] = useState({
    loading: false,
    error: null,
    rows: [],
  });
  const [invoices, setInvoices] = useState({
    loading: false,
    error: null,
    items: [],
    total: 0,
    offset: 0,
  });
  // The tenancy whose data is loaded or loading. Guards both a repeat fetch on
  // every tab switch and a response for a previously selected row landing
  // after a newer one.
  const loadedFor = useRef(null);

  const loadRevenue = useCallback((id) => {
    setRevenue({ loading: true, error: null, rows: [] });
    fetchAdminTenancyRevenue(id)
      .then((d) => {
        if (loadedFor.current !== id) return;
        // `GET /tenancies/{id}/revenue` is declared `List[dict]` and returns a
        // bare array — not an envelope. Guarded rather than trusted, because a
        // non-array here would otherwise reach the breakdown helpers.
        setRevenue({
          loading: false,
          error: null,
          rows: Array.isArray(d) ? d : [],
        });
      })
      .catch((e) => {
        if (loadedFor.current !== id) return;
        setRevenue({
          loading: false,
          error: e?.message || "Mietzins-Positionen konnten nicht geladen werden.",
          rows: [],
        });
      });
  }, []);

  const loadInvoices = useCallback((id, offset) => {
    setInvoices((s) => ({ ...s, loading: true, error: null, offset }));
    fetchBexioTenancyInvoices({ tenancyId: id, limit: PAGE_SIZE, offset })
      .then((d) => {
        if (loadedFor.current !== id) return;
        const items = Array.isArray(d?.items) ? d.items : [];
        setInvoices({
          loading: false,
          error: null,
          items,
          total: d?.total ?? items.length,
          offset,
        });
      })
      .catch((e) => {
        if (loadedFor.current !== id) return;
        setInvoices({
          loading: false,
          error: e?.message || "Bexio-Rechnungen konnten nicht geladen werden.",
          items: [],
          total: 0,
          offset,
        });
      });
  }, []);

  useEffect(() => {
    if (!active || !tenancyId) return;
    if (loadedFor.current === tenancyId) return;
    loadedFor.current = tenancyId;
    loadRevenue(tenancyId);
    loadInvoices(tenancyId, 0);
  }, [active, tenancyId, loadRevenue, loadInvoices]);

  // A different tenancy while this tab is closed: forget the old result so it
  // is never shown under the new contract, and let the effect above reload it
  // when the tab is opened again.
  useEffect(() => {
    if (loadedFor.current && loadedFor.current !== tenancyId) {
      loadedFor.current = null;
      setRevenue({ loading: false, error: null, rows: [] });
      setInvoices({ loading: false, error: null, items: [], total: 0, offset: 0 });
    }
  }, [tenancyId]);

  const handleOffsetChange = useCallback(
    (offset) => {
      if (!tenancyId) return;
      loadInvoices(tenancyId, offset);
    },
    [tenancyId, loadInvoices]
  );

  return (
    <div className="flex flex-col gap-3" data-testid="tenancy-finance">
      <ContractValuesCard tenancy={tenancy} revenue={revenue} />
      <InvoicesCard invoices={invoices} onOffsetChange={handleOffsetChange} />
    </div>
  );
}
